import styled from '@emotion/styled';
import { InfoTooltipIcon } from '@resistapp/client/components/icons/info-tooltip-icon';
import { getGroupPalette } from '@resistapp/client/components/shared/palettes';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import {
  BarDatum,
  BarOrBoxPlotData,
  getBarDatumKeys,
  PlotDatum,
  ResearchPlotData,
} from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useExperimentalMetric } from '@resistapp/client/hooks/use-experimental-metric';
import { friendlyDate, friendlyMonth, type StandardDateFormat } from '@resistapp/common/friendly';
import { isConsideredAbsolute } from '@resistapp/common/normalisation-mode';
import { getAbundanceStatsMax, getAbundanceStatsMin } from '@resistapp/common/statistics/abundance-stats';
import { BioRep, FullProject, XAxisMode } from '@resistapp/common/types';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { AxisScale } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal, scaleUtc } from '@visx/scale';
import { useTooltipInPortal } from '@visx/tooltip';
import { addDays, addSeconds, differenceInDays, differenceInMonths, max, min } from 'date-fns';
import { chain, compact, flatten, get, sortBy, uniq } from 'lodash';
import { useMemo } from 'react';
import { theme } from '../../shared/theme';
import { BoxPlotMeanLine, ReferenceLevelTooltipContent } from '../box-plot-reference-level-line';
import {
  BarBoxOrdinalOrTimeAxisX,
  BarPlotDetectedGenesAxisY,
  BoxPlotRelativeAbundanceAxisY,
  HeatMapGenesAxisY,
} from './axes';
import { DataGroups, ExperimentalMetric } from './data-groups';
import { chartLayoutValues, getHeatmapChartOneGeneHeight, getShortestStepInSeconds } from './utils';

export type BarBoxOrHeatPlotProps = {
  width: number;
  type: 'bar' | 'box' | 'heat';
  chartId?: string;
  showSampleNumbers: boolean;
  project: FullProject;
  showOnlyTicks: boolean;
};

export type ScaleTime = (n: Date) => number;
export type ScaleBand = (n: string) => number;

export const barPlotHeight = 348; // Height to accommodate legend items and plot content
export const boxPlotHeight = 400;

export function MultiPlot(props: BarBoxOrHeatPlotProps) {
  const { width, type, chartId, showSampleNumbers, project, showOnlyTicks } = props;
  const {
    loading,
    plotData,
    consistentBioGroupNumbering,
    consistentBioNumbering,
    sequentialSampleIndexes,
    getReferenceLevel,
  } = useResearchContext();
  const { allGeneGroups } = useAssayContext();
  const experimentalMetric = useExperimentalMetric();
  const tooltipStuff = useTooltipInPortal({
    scroll: false,
    detectBounds: true,
  });
  const { queryFilters } = useSampleDataContext();

  // Determine if bioRep letters should be appended to sample numbers.
  const showBioRepIndicator = useMemo(() => {
    if (!showSampleNumbers) return false;
    if (queryFilters.filters.xAxisMode !== XAxisMode.ORDINAL_INDIVIDUAL) return false;
    const allSamples = flattenSamplesByUID(project.samplesByUID);
    return (
      chain(allSamples)
        .map(s => s.bioRep)
        .uniq()
        .value().length > 1
    );
  }, [showSampleNumbers, queryFilters.filters.xAxisMode, project.samplesByUID]);

  // Precompute bioRep lookup by sample id for quick access inside getLabel.
  const bioRepBySampleId = useMemo(() => {
    const flatSamples = flattenSamplesByUID(project.samplesByUID);
    return flatSamples.reduce<Record<number, BioRep>>((acc, sample) => {
      acc[sample.id] = sample.bioRep;
      return acc;
    }, {});
  }, [project.samplesByUID]);

  // Collecting the genes present in the heatmap scans every heatmap datum, so do it once per plot data
  const heatData = plotData?.heatData;
  const sortIdxByGene = plotData?.sortIdxByGene;
  const heatmapGenes = useMemo(
    () =>
      type === 'heat' && sortIdxByGene
        ? sortBy(
            uniq(flatten((heatData || []).map(datums => datums.flatMap(datum => getBarDatumKeys(datum))))),
            gene => sortIdxByGene[gene] ?? Number.MAX_SAFE_INTEGER,
          )
        : [],
    [type, heatData, sortIdxByGene],
  );

  if (loading || !plotData) {
    return null;
  }

  const { againstTime, geneGroups, numAnalyzed } = plotData;

  const tooltipRef = tooltipStuff.containerRef;
  const data: PlotDatum[][] = getPlotData(type, plotData);
  const isBarShapedPlot = type !== 'box';
  const barDatumKeys = getBarDatumKeys(get(data, [0, 0], []) as BarDatum);
  const numKeys = type === 'heat' ? heatmapGenes.length : barDatumKeys.length;
  const singleGeneHeatBoxHeight = getHeatmapChartOneGeneHeight(numKeys);
  const height = getChartHeight(type, numKeys);

  // Common: Margins and widths
  const verticalOffset = 12; // Pixels, for the upper halves of the first axis marker possible outlier dots
  const xMax = width - chartLayoutValues.plotLeftOffset; // In pixels, how many pixels from left to right the drawing area spans.

  const strDates = compact(flatten(data.map(datums => datums.map(datum => datum.date))));
  const dates = strDates.map(s => new Date(s));
  const timeDomain = againstTime ? [addDays(min(dates), -2), addDays(max(dates), 2)] : []; // Leave some slack, but not much (the time margin may have filtered samples)

  const yMax = height - verticalOffset * 2;

  // Common: time or ordinal x-axis scale
  const borderRoundingRadius = 10;
  const range: [number, number] = [borderRoundingRadius * 1.5, xMax - borderRoundingRadius * 1.5];
  const xScale: AxisScale<number> = againstTime
    ? scaleUtc({
        domain: timeDomain,
        range,
      })
    : scaleBand<string>({
        domain: data.map((_, i) => `${i}`), // groups not supported in ordinal plotting
        range,
        padding: 0.2, // Add padding between bars
      });

  const getReferenceDatum = (index: number): PlotDatum | undefined => {
    const boxDatum = get(data, [index, 0]) as PlotDatum | undefined;
    if (boxDatum) return boxDatum;
    const barDatum = get(plotData.barData, [index, 0]) as PlotDatum | undefined;
    if (barDatum) return barDatum;
    return get(plotData.heatData, [index, 0]) as PlotDatum | undefined;
  };

  // Common: x-axis formatting
  const getXAxisParameters = () => {
    if (!againstTime) {
      const dontShowSampleNumbers = !showSampleNumbers;
      return {
        xTicks: data.length,
        getLabel: dontShowSampleNumbers
          ? (x: string) => {
              const datum = getReferenceDatum(+x);
              return datum?.label ?? '';
            }
          : (x: string) => {
              const datum = getReferenceDatum(+x);
              if (!datum) return '';
              const parsedSampleId = Number(datum.sampleId);
              const hasSampleId = Number.isFinite(parsedSampleId);
              const sequentialIndex = hasSampleId ? sequentialSampleIndexes[parsedSampleId] : undefined;
              const sequentialNumber = sequentialIndex != null ? `${sequentialIndex}.` : '';
              const sampleNumber = datum.sampleNumber;
              const bioRep = hasSampleId ? bioRepBySampleId[parsedSampleId] : undefined;
              const defaultLabel = datum.label;
              if (showBioRepIndicator && consistentBioNumbering) {
                if (bioRep) {
                  return `${sampleNumber}${bioRep}`;
                }
                return sampleNumber || sequentialNumber || defaultLabel;
              }
              if (!showBioRepIndicator && consistentBioGroupNumbering) {
                return sampleNumber || sequentialNumber || defaultLabel;
              }
              return sequentialNumber || sampleNumber || defaultLabel;
            },
      };
    } else {
      const numDays = differenceInDays(timeDomain[1], timeDomain[0]);
      const showDatesOnAxis = numDays <= 40 || differenceInMonths(timeDomain[1], timeDomain[0]) <= 1;
      const maxLabels = showDatesOnAxis
        ? differenceInDays(timeDomain[1], timeDomain[0])
        : differenceInMonths(timeDomain[1], timeDomain[0]);
      const labelWidth = showDatesOnAxis ? 80 : 40; // 2022 or 30. Oct
      const numLabelsThatFit = Math.floor(xMax / labelWidth); // Maximum number of labels that can fit without overlapping
      return {
        xTicks: Math.min(maxLabels, numLabelsThatFit), // Use the smaller of calculated maxLabels or maxPossibleLabels
        getLabel: (x: string) => {
          return showDatesOnAxis
            ? friendlyDate(x as StandardDateFormat, 'none')
            : friendlyMonth(x as StandardDateFormat, 'replaceJan');
        },
      };
    }
  };

  const { xTicks, getLabel } = getXAxisParameters();

  // Common: group spacing
  const shortestTimeStep = getShortestStepInSeconds(dates);
  const maxGroupWidth = 60;
  const minGroupWidth = 10;
  const groupWidth = againstTime
    ? Math.max(
        minGroupWidth,
        Math.min(maxGroupWidth, xScale(addSeconds(timeDomain[0], shortestTimeStep)) || maxGroupWidth),
      )
    : Math.max(minGroupWidth, Math.min(maxGroupWidth, xMax / data.length - minGroupWidth));

  // Common: for some reason, ordinal/linear xScale slightly misaligns bar compared to ticks without help
  const ordinalAligmentFixup =
    againstTime || !data.length
      ? 0
      : data.length === 1
        ? xMax / 3 - 10
        : 0.4 * ((xScale as ScaleBand)('1') - (xScale as ScaleBand)('0')) - 2;

  // Plot specific: y-axis and box color scale
  const extremeYValue =
    type !== 'box'
      ? Infinity
      : isConsideredAbsolute(plotData.normalisationMode)
        ? getAbundanceStatsMax(flatten(plotData.boxData))
        : getAbundanceStatsMin(flatten(plotData.boxData));

  const yScale = scaleLinear<number>({
    range: [yMax, 0],
    round: true,
    clamp: true,
    domain: isBarShapedPlot
      ? [0, numAnalyzed]
      : isConsideredAbsolute(plotData.normalisationMode)
        ? [0, Math.ceil(extremeYValue)]
        : [Math.floor(extremeYValue), 0],
  });

  // Bar plot specific:
  const palette = isBarShapedPlot ? getGroupPalette(geneGroups, plotData.grouping, allGeneGroups) : undefined;
  const colorScale = isBarShapedPlot
    ? scaleOrdinal<string, string>({
        domain: geneGroups,
        range: palette,
      })
    : undefined;

  // For some reason the heatmap needs more height than the others
  const groupHeight = yMax + (type === 'heat' ? 66 : 0);

  return width < 10 ? null : (
    <RelativeDiv>
      <svg
        ref={tooltipRef}
        width={width}
        height={height + chartLayoutValues.xAxisHeight}
        style={{ overflow: 'visible' }}
        id={chartId}
      >
        <Group top={verticalOffset} left={chartLayoutValues.plotLeftOffset}>
          <rect
            x={0}
            y={-2}
            width={width - chartLayoutValues.plotLeftOffset}
            height={groupHeight + 2}
            fill="none"
            stroke={theme.colors.neutral400}
            rx={borderRoundingRadius}
            ry={borderRoundingRadius}
          />

          {type === 'heat' && queryFilters.filters.selectedTargetGrouping !== 'assay' && (
            <HeatMapGenesAxisY
              genes={heatmapGenes}
              colorScale={colorScale}
              grouping={plotData.grouping}
              singleGeneHeatBoxHeight={singleGeneHeatBoxHeight}
            />
          )}

          {type === 'box' ? (
            <BoxPlotRelativeAbundanceAxisY yScale={yScale} yMax={yMax} xMax={xMax} extremeYValue={extremeYValue} />
          ) : (
            type === 'bar' && <BarPlotDetectedGenesAxisY yScale={yScale} yMax={yMax} xMax={xMax} />
          )}
          <DataGroups
            data={data as BarOrBoxPlotData}
            tooltipStuff={tooltipStuff}
            xScale={xScale}
            yScale={yScale}
            againstTime={againstTime}
            groupWidth={groupWidth}
            ordinalAligmentFixup={ordinalAligmentFixup}
            grouping={plotData.grouping}
            barPlotGeneGroups={isBarShapedPlot ? geneGroups : undefined}
            colorScale={colorScale}
            type={type}
            sortIdxByGene={plotData.sortIdxByGene}
            singleGeneHeatBoxHeight={singleGeneHeatBoxHeight}
            showSampleNumbers={showSampleNumbers}
            project={project}
          />
          {type === 'box' && <BoxPlotMeanLine yScale={yScale} width={xMax} left={0} />}
        </Group>
        {type !== 'heat' && (
          <BarBoxOrdinalOrTimeAxisX
            width={width}
            height={height}
            xScale={xScale}
            getLabel={getLabel}
            againstTime={againstTime}
            numTicks={xTicks}
            showOnlyTicks={showOnlyTicks}
          />
        )}
      </svg>
      {type === 'box' &&
        (() => {
          const [referenceValue, environmentTypeOrSubtype] = getReferenceLevel();
          if (referenceValue == null) return null;

          // Determine how close log10(referenceValue) is to the nearest integer.
          // If it is very close (< 0.1), the label text is likely adjacent,
          // so we push the tooltip icon further left to avoid overlap.
          const logVal = Math.log10(referenceValue);
          const fractionalDistance = Math.abs(logVal - Math.round(logVal));
          const iconLeftOffset = fractionalDistance < 0.134 ? 66 : 36; // px
          const iconLeft = chartLayoutValues.plotLeftOffset - iconLeftOffset;

          // Compute vertical position: inside svg the group is offset by verticalOffset
          // Subtract ~23px to vertically centre the icon on the dashed line.
          const iconTop = verticalOffset + yScale(logVal) - 23;

          return (
            <InfoTooltipIcon placement="right" style={{ position: 'absolute', top: iconTop, left: iconLeft }}>
              <ReferenceLevelTooltipContent
                plotData={plotData}
                selectedTargets={queryFilters.filters.selectedTargets}
                environmentTypeOrSubtype={environmentTypeOrSubtype}
              />
            </InfoTooltipIcon>
          );
        })()}
      {type === 'box' && experimentalMetric ? (
        <ExperimentalMetric top={265} left={0} value={experimentalMetric} groupWidth={40} />
      ) : null}
    </RelativeDiv>
  );
}

export function getChartHeight(type: string, numKeys: number) {
  return type === 'heat' ? 10 * numKeys : type === 'bar' ? barPlotHeight : boxPlotHeight;
}

const RelativeDiv = styled.div`
  position: relative;
`;

/**
 * Function to retrieve plot data based on the type of plot.
 * @param {string} type - The type of plot data to retrieve ('bar', 'box', or 'heat').
 * @returns {Array} - The corresponding plot data array.
 */
const getPlotData = (type: string, plotData: ResearchPlotData) => {
  switch (type) {
    case 'bar':
      return plotData.barData || [];
    case 'box':
      return plotData.boxData || [];
    case 'heat':
      return plotData.heatData || [];
    default:
      return [];
  }
};
